import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Input,
  Button,
  Breadcrumb,
  Modal,
  message,
  Form,
  Select,
  Checkbox,
  Space,
  Row,
  Col,
  Grid,
} from 'antd';
import { Link } from 'react-router-dom';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { LoadableContent } from '../../components';
import { UserService } from '../../common/services';
import Can from '../../components/can';

const ListeDesAdministeurs = () => {
  const [admins, setAdmins] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal édition
  const [editOpen, setEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();

  // Permissions & presets depuis le backend
  const [permList, setPermList] = useState([]);
  const [groupOrder, setGroupOrder] = useState([]);
  const [groupLabels, setGroupLabels] = useState({});
  const [rolePresets, setRolePresets] = useState({});

  // Roles proposés
  const roleOptions = useMemo(
    () => ([
      { value: 'directeur', label: 'Directeur' },
      { value: 'support', label: 'Support' },
      { value: 'comptable', label: 'Comptable' },
      { value: 'commercial', label: 'Commercial' },
      { value: 'admin', label: 'Admin' },
    ]),
    []
  );

  // 🔹 Breakpoints (responsive helpers)
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm; // < 576px
  const controlSize = isMobile ? 'small' : 'middle';

  useEffect(() => {
    setLoading(true);
    Promise.all([
      UserService.listeDesAdministrateurs(),
      UserService.getPermissions().then(r => r?.data || r),
      UserService.getRolePresets().then(r => r?.data || r),
    ])
      .then(([adminsData, permsData, presetsData]) => {
        setAdmins(Array.isArray(adminsData) ? adminsData : []);

        const list = Array.isArray(permsData?.list)
          ? permsData.list
          : (permsData?.all || []).map(k => ({ key: k, label: k, group: null, groupLabel: null }));

        setPermList(list);
        setGroupLabels(permsData?.groupLabels || {});
        setGroupOrder([...new Set(list.map(i => i.group).filter(Boolean))]);

        setRolePresets(presetsData || {});
      })
      .catch((err) => {
        console.error('Erreur chargement admins/permissions/presets :', err);
        message.error('Erreur de chargement');
      })
      .finally(() => setLoading(false));
  }, []);

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let local = phone.replace('+225', '').trim();
    return local.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  };

  // Recherche filtrée
  const filteredAdmins = admins.filter((admin) => {
    if (!admin) return false;
    const { phone = '', firstName = '', lastName = '', id = '', role = '' } = admin;
    const search = (searchTerm || '').toLowerCase();
    return (
      phone.toLowerCase().includes(search) ||
      firstName.toLowerCase().includes(search) ||
      lastName.toLowerCase().includes(search) ||
      role.toLowerCase().includes(search) ||
      id.toLowerCase().includes(search)
    );
  });

  // Suppression
  const handleDelete = (administrateur) => {
    const { _id, firstName, lastName, phone } = administrateur;

    Modal.confirm({
      title: 'Confirmer la suppression',
      content: (
        <>
          <p>Êtes-vous sûr de vouloir supprimer cet administrateur ?</p>
          <p><strong>Nom :</strong> {firstName} {lastName}</p>
          <p><strong>Téléphone :</strong> {formatPhoneNumber(phone)}</p>
        </>
      ),
      okText: 'Oui, supprimer',
      cancelText: 'Annuler',
      okButtonProps: { danger: true, size: controlSize, style: isMobile ? { width: '100%' } : {} },
      cancelButtonProps: { size: controlSize, style: isMobile ? { width: '100%' } : {} },
      centered: true,
      width: screens.lg ? 520 : 420,
      bodyStyle: { padding: isMobile ? 12 : 24 },
      onOk: async () => {
        try {
          await UserService.deleteAdministrateur(_id);
          message.success('Administrateur supprimé avec succès !');
          setAdmins(prev => prev.filter(p => p._id !== _id));
        } catch (error) {
          console.error('Erreur lors de la suppression :', error);
          message.error('Une erreur est survenue pendant la suppression.');
        }
      }
    });
  };

  // Ouvrir modal d’édition
  const openEdit = (record) => {
    setEditingUser(record);
    form.setFieldsValue({
      firstName: record.firstName,
      lastName: record.lastName,
      phone: (record.phone || '').replace('+225', ''),
      role: record.role || 'admin',
      permissions: record.permissions || rolePresets[record.role] || [],
      email: record.email || '',
      residence: record.residence || '',
      country: record.country || 'civ',
    });
    setEditOpen(true);
  };

  // Changement de rôle -> appliquer preset
  const onRoleChange = (r) => {
    const preset = rolePresets?.[r] || [];
    form.setFieldsValue({ role: r, permissions: preset });
  };

  // Submit édition
  const onEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values, phone: `+225${values.phone}` };

      const body = {
        firstName: payload.firstName,
        lastName: payload.lastName,
        phone: payload.phone,
        email: payload.email,
        residence: payload.residence,
        country: payload.country,
        role: payload.role,
        permissions: payload.permissions,
      };

      await UserService.miseAjourRoleEtPermissions(editingUser._id, body);
      message.success('Administrateur mis à jour !');
      setEditOpen(false);

      // MAJ locale
      setAdmins(prev => prev.map(u => (u._id === editingUser._id ? { ...u, ...body } : u)));
    } catch (e) {
      if (e?.errorFields) return; // erreurs de validation
      console.error(e);
      message.error('Échec de la mise à jour');
    }
  };

  // Colonnes (on ajoute le scroll horizontal via Table.scroll)
  const columns = [
    {
      title: 'Numéro de téléphone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => formatPhoneNumber(phone),
      sorter: (a, b) => (a.phone || '').localeCompare(b.phone || ''),
      width: 160,
    },
    {
      title: 'Nom',
      dataIndex: 'firstName',
      key: 'firstName',
      sorter: (a, b) => (a.firstName || '').localeCompare(b.firstName || ''),
      width: 140,
    },
    {
      title: 'Prénom(s)',
      dataIndex: 'lastName',
      key: 'lastName',
      sorter: (a, b) => (a.lastName || '').localeCompare(b.lastName || ''),
      width: 160,
    },
    {
      title: 'Rôle',
      dataIndex: 'role',
      key: 'role',
      sorter: (a, b) => (a.role || '').localeCompare(b.role || ''),
      width: 130,
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''),
      width: 180,
      responsive: ['sm'], // masque sur très petit écran
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: isMobile ? undefined : 'right',
      width: 120,
      render: (_, record) => (
        <Space wrap size={[8, 8]}>
          <Can perm="users:update">
            <Button icon={<EditOutlined />} size={controlSize} onClick={() => openEdit(record)} />
          </Can>
          <Can perm="users:delete">
            <Button
              danger
              icon={<DeleteOutlined />}
              size={controlSize}
              onClick={() => handleDelete(record)}
            />
          </Can>
        </Space>
      ),
    },
  ];

  return (
    <LoadableContent isLoading={isLoading}>
      <div className="content-wrapper">
        <div className="container-xxl flex-grow-1 container-p-y">
          <div className="row">
            <div className="col-md-12">
              <Breadcrumb
                style={{ marginBottom: 16 }}
                items={[
                  { title: <Link to="/dashboard" style={{ fontWeight: 'bold' }}>🏠 ACCUEIL</Link> },
                  { title: <span style={{ fontWeight: 'bold' }}>👥 LISTE DES ADMINISTRATEURS</span> },
                ]}
              />

              <div className="card mb-4">
                <div className="card-body">
                  {/* 🔎 Barre de recherche responsive */}
                  <Row justify="end" gutter={[12, 12]} style={{ marginBottom: 12 }}>
                    <Col xs={24} sm={16} md={12} lg={8}>
                      <Input
                        placeholder="Rechercher par nom, prénom, rôle ou numéro"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        allowClear
                        size={controlSize}
                      />
                    </Col>
                  </Row>

                  {/* 📊 Table responsive */}
                  <Table
                    rowKey={(p) => p._id?.toString()}
                    columns={columns}
                    dataSource={filteredAdmins}
                    size={controlSize}
                    scroll={{ x: true }}
                    pagination={{
                      pageSize: 10,
                      showLessItems: isMobile,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ✏️ MODAL EDIT responsive */}
        <Modal
          title="Modifier l’administrateur"
          open={editOpen}
          okText="Enregistrer"
          onOk={onEditSubmit}
          onCancel={() => setEditOpen(false)}
          destroyOnClose
          centered
          width={screens.xl ? 820 : screens.lg ? 720 : screens.md ? 600 : 420}
          bodyStyle={{ padding: isMobile ? 12 : 24 }}
          okButtonProps={{ size: controlSize, style: isMobile ? { width: '100%' } : {} }}
          cancelButtonProps={{ size: controlSize, style: isMobile ? { width: '100%' } : {} }}
        >
          <Form form={form} layout="vertical">
            {/* 🧰 Infos principales */}
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12} lg={8}>
                <Form.Item name="firstName" label="Nom" rules={[{ required: true }]}>
                  <Input size={controlSize} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item name="lastName" label="Prénom" rules={[{ required: true }]}>
                  <Input size={controlSize} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item
                  name="phone"
                  label="Téléphone"
                  rules={[
                    { required: true },
                    { pattern: /^[0-9]{10}$/, message: '10 chiffres' },
                  ]}
                >
                  <Input addonBefore="+225" maxLength={10} size={controlSize} />
                </Form.Item>
              </Col>

              <Col xs={24} md={12} lg={8}>
                <Form.Item name="email" label="Email">
                  <Input size={controlSize} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item name="residence" label="Résidence">
                  <Input size={controlSize} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} lg={8}>
                <Form.Item name="country" label="Pays">
                  <Input size={controlSize} />
                </Form.Item>
              </Col>

              <Col xs={24} md={12} lg={8}>
                <Form.Item name="role" label="Rôle" rules={[{ required: true }]} initialValue="admin">
                  <Select
                    onChange={onRoleChange}
                    options={roleOptions}
                    size={controlSize}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* 🔐 Permissions (groupées, grille responsive) */}
            <div style={{ marginTop: 8 }}>
              <div className="d-flex justify-content-between" style={{ marginBottom: 8 }}>
                <label style={{ fontWeight: 600 }}>Permissions</label>
                <Button
                  size="small"
                  onClick={() => {
                    const r = form.getFieldValue('role');
                    form.setFieldsValue({ permissions: rolePresets?.[r] || [] });
                  }}
                >
                  Réinitialiser selon le rôle
                </Button>
              </div>

              <Form.Item name="permissions" noStyle>
                <Checkbox.Group style={{ width: '100%' }}>
                  {groupOrder.map((g) => {
                    const items = permList.filter(p => p.group === g);
                    if (!items.length) return null;
                    return (
                      <div key={g} style={{ marginBottom: 12 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                          {groupLabels?.[g] || g}
                        </div>
                        <Row gutter={[8, 8]}>
                          {items.map(p => (
                            <Col key={p.key} xs={24} sm={12} md={8}>
                              <Checkbox value={p.key}>{p.label}</Checkbox>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    );
                  })}

                  {/* Groupe "Autres" */}
                  {permList.some(p => !p.group) && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Autres</div>
                      <Row gutter={[8, 8]}>
                        {permList.filter(p => !p.group).map(p => (
                          <Col key={p.key} xs={24} sm={12} md={8}>
                            <Checkbox value={p.key}>{p.label}</Checkbox>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                </Checkbox.Group>
              </Form.Item>
            </div>
          </Form>
        </Modal>
      </div>
    </LoadableContent>
  );
};

export default ListeDesAdministeurs;
